import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../../api/client';
import { Project, PackagingRecord, BoxType } from '../../types';
import {
  X, Package, Box, Weight, FileText, Loader2, CheckCircle2, Truck, Hash,
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const BOX_TYPE_OPTIONS: { value: BoxType; label: string; icon: string }[] = [
  { value: 'caja_pequena', label: 'Caja pequeña', icon: '📦' },
  { value: 'caja_mediana', label: 'Caja mediana', icon: '📦' },
  { value: 'caja_grande',  label: 'Caja grande',  icon: '🗃️' },
  { value: 'sobre',        label: 'Sobre',        icon: '✉️' },
  { value: 'palet',        label: 'Palet',        icon: '🪵' },
  { value: 'otro',         label: 'Otro',         icon: '📫' },
];

const CARRIER_OPTIONS = [
  { value: 'correos', label: 'Correos' },
  { value: 'mrw',     label: 'MRW' },
  { value: 'seur',    label: 'SEUR' },
  { value: 'gls',     label: 'GLS' },
  { value: 'dhl',     label: 'DHL' },
  { value: 'ups',     label: 'UPS' },
  { value: 'nacex',   label: 'Nacex' },
  { value: 'otro',    label: 'Otro' },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface PackagingFormModalProps {
  project: Project;
  onClose: () => void;
  onSuccess: (projectId: number) => void;
}

// ─── Input Field ──────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  icon: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
  hint?: string;
}

function Field({ label, icon, required, children, hint }: FieldProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
        color: '#8b89a0', letterSpacing: '0.06em', fontWeight: 600,
      }}>
        {icon}
        {label}
        {required && <span style={{ color: '#f97316', marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#4a4858', fontFamily: 'JetBrains Mono, monospace' }}>
          {hint}
        </span>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: '#0d0d15',
  border: '1px solid #2a2838',
  borderRadius: 8,
  padding: '10px 12px',
  color: '#e2e0f0',
  fontSize: 13,
  fontFamily: 'DM Sans, sans-serif',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

// ─── Main Component ───────────────────────────────────────────────────────────

export function PackagingFormModal({ project, onClose, onSuccess }: PackagingFormModalProps) {
  const queryClient = useQueryClient();

  const embalajeStep = project.steps.find(s => s.department === 'embalaje');

  // Prefill from existing step data if available
  const existingData = (embalajeStep?.data as any)?.packaging as Partial<PackagingRecord> | undefined;

  const [boxType, setBoxType]               = useState<BoxType>(existingData?.box_type ?? 'caja_mediana');
  const [bundleCount, setBundleCount]       = useState<string>(existingData?.bundle_count?.toString() ?? '1');
  const [estimatedWeight, setEstimatedWeight] = useState<string>(existingData?.estimated_weight?.toString() ?? '');
  const [notes, setNotes]                   = useState<string>(existingData?.notes ?? '');
  const [trackingNumber, setTrackingNumber] = useState<string>('');
  const [shippingCarrier, setShippingCarrier] = useState<string>('');
  const [focusedField, setFocusedField]     = useState<string | null>(null);
  const [submitted, setSubmitted]           = useState(false);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!embalajeStep) throw new Error('Paso de embalaje no encontrado');
      const record: PackagingRecord = {
        box_type: boxType,
        bundle_count: parseInt(bundleCount, 10),
        estimated_weight: parseFloat(estimatedWeight),
        notes: notes.trim() || undefined,
        tracking_number: trackingNumber.trim() || undefined,
        shipping_carrier: shippingCarrier || undefined,
      };
      return projectsApi.savePackaging(project.id, embalajeStep.id, record);
    },
    onSuccess: () => {
      setSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setTimeout(() => {
        onSuccess(project.id);
      }, 1200);
    },
  });

  const isValid =
    bundleCount !== '' &&
    parseInt(bundleCount, 10) >= 1 &&
    estimatedWeight !== '' &&
    parseFloat(estimatedWeight) > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || mutation.isPending || submitted) return;
    mutation.mutate();
  };

  const focusedBorder = '#f97316';

  return (
    // Overlay
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        backdropFilter: 'blur(4px)',
      }}
    >
      {/* Modal */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#12121a',
          border: '1px solid #1e1e2e',
          borderRadius: 20,
          width: '100%',
          maxWidth: 480,
          boxShadow: '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(249,115,22,0.08)',
          overflow: 'hidden',
          animation: 'modalIn 0.2s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Header accent bar */}
        <div style={{
          height: 3,
          background: 'linear-gradient(90deg, #f97316, #ea580c)',
        }} />

        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          padding: '20px 24px 16px',
          borderBottom: '1px solid #1a1a28',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Package size={20} color="#f97316" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#e8e6f4', fontFamily: 'Syne, sans-serif', letterSpacing: '-0.02em' }}>
                Registrar Embalaje
              </div>
              <div style={{ fontSize: 11, color: '#6b6880', fontFamily: 'JetBrains Mono, monospace', marginTop: 2 }}>
                {project.reference} — {project.title}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#4a4858', padding: 6, borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = '#e2e0f0')}
            onMouseLeave={e => (e.currentTarget.style.color = '#4a4858')}
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Box type */}
          <Field label="TIPO DE CAJA" icon={<Box size={12} />} required>
            <select
              value={boxType}
              onChange={e => setBoxType(e.target.value as BoxType)}
              onFocus={() => setFocusedField('box_type')}
              onBlur={() => setFocusedField(null)}
              style={{
                ...inputStyle,
                borderColor: focusedField === 'box_type' ? focusedBorder : '#2a2838',
                cursor: 'pointer',
              }}
            >
              {BOX_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value} style={{ background: '#12121a' }}>
                  {opt.icon} {opt.label}
                </option>
              ))}
            </select>
          </Field>

          {/* Bundle count + Weight — side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="NÚMERO DE BULTOS" icon={<Package size={12} />} required hint="Mínimo 1">
              <input
                type="number"
                min={1}
                step={1}
                value={bundleCount}
                onChange={e => setBundleCount(e.target.value)}
                onFocus={() => setFocusedField('bundle_count')}
                onBlur={() => setFocusedField(null)}
                placeholder="1"
                style={{
                  ...inputStyle,
                  borderColor: focusedField === 'bundle_count' ? focusedBorder : '#2a2838',
                }}
              />
            </Field>

            <Field label="PESO ESTIMADO" icon={<Weight size={12} />} required hint="En kilogramos">
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={estimatedWeight}
                  onChange={e => setEstimatedWeight(e.target.value)}
                  onFocus={() => setFocusedField('estimated_weight')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="0.0"
                  style={{
                    ...inputStyle,
                    borderColor: focusedField === 'estimated_weight' ? focusedBorder : '#2a2838',
                    paddingRight: 36,
                  }}
                />
                <span style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: '#6b6880',
                  pointerEvents: 'none',
                }}>
                  kg
                </span>
              </div>
            </Field>
          </div>

          {/* Notes */}
          <Field label="NOTAS" icon={<FileText size={12} />} hint="Opcional — instrucciones especiales, frágil, etc.">
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onFocus={() => setFocusedField('notes')}
              onBlur={() => setFocusedField(null)}
              placeholder="Ej: Material frágil, proteger esquinas..."
              rows={3}
              style={{
                ...inputStyle,
                borderColor: focusedField === 'notes' ? focusedBorder : '#2a2838',
                resize: 'vertical',
                minHeight: 80,
              }}
            />
          </Field>

          {/* Tracking — side by side */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="NÚMERO DE SEGUIMIENTO" icon={<Hash size={12} />} hint="Opcional">
              <input
                type="text"
                value={trackingNumber}
                onChange={e => setTrackingNumber(e.target.value)}
                onFocus={() => setFocusedField('tracking_number')}
                onBlur={() => setFocusedField(null)}
                placeholder="Ej: ES123456789ES"
                style={{
                  ...inputStyle,
                  borderColor: focusedField === 'tracking_number' ? focusedBorder : '#2a2838',
                }}
              />
            </Field>

            <Field label="TRANSPORTISTA" icon={<Truck size={12} />} hint="Opcional">
              <select
                value={shippingCarrier}
                onChange={e => setShippingCarrier(e.target.value)}
                onFocus={() => setFocusedField('shipping_carrier')}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...inputStyle,
                  borderColor: focusedField === 'shipping_carrier' ? focusedBorder : '#2a2838',
                  cursor: 'pointer',
                }}
              >
                <option value="" style={{ background: '#12121a' }}>Sin transportista</option>
                {CARRIER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value} style={{ background: '#12121a' }}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* Error */}
          {mutation.isError && (
            <div style={{
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
              borderRadius: 8, padding: '10px 14px',
              fontSize: 12, color: '#f87171',
              fontFamily: 'DM Sans, sans-serif',
            }}>
              Error al guardar. Verifica los datos e inténtalo de nuevo.
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                background: 'transparent',
                border: '1px solid #2a2838',
                borderRadius: 10,
                padding: '11px 16px',
                color: '#8b89a0',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#3a3850'; e.currentTarget.style.color = '#e2e0f0'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2838'; e.currentTarget.style.color = '#8b89a0'; }}
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={!isValid || mutation.isPending || submitted}
              style={{
                flex: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: submitted
                  ? 'linear-gradient(135deg, #22c55e, #16a34a)'
                  : !isValid || mutation.isPending
                  ? '#1c1b28'
                  : 'linear-gradient(135deg, #f97316, #ea580c)',
                border: 'none',
                borderRadius: 10,
                padding: '11px 16px',
                color: !isValid && !submitted ? '#4a4858' : '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: !isValid || mutation.isPending || submitted ? 'not-allowed' : 'pointer',
                fontFamily: 'DM Sans, sans-serif',
                letterSpacing: '0.02em',
                boxShadow: isValid && !mutation.isPending && !submitted
                  ? '0 4px 14px rgba(249,115,22,0.35)'
                  : 'none',
                transition: 'all 0.18s',
              }}
            >
              {submitted ? (
                <><CheckCircle2 size={15} /> Guardado</>
              ) : mutation.isPending ? (
                <><Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> Guardando...</>
              ) : (
                <><Package size={15} /> Registrar Embalaje</>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { opacity: 0.4; }
        select option { background: #12121a; color: #e2e0f0; }
      `}</style>
    </div>
  );
}
